import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
} from "react";
import { createPortal } from "react-dom";
import type { SwitcherProject } from "@/components/panels/space-switcher";
import { Icons } from "@/components/ui/icons";
import { ProjectAvatar } from "@/components/ui/project-avatar";
import { Button, ContextMenu, ContextMenuTrigger } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  createProjectFromUser,
  selectProjectFromUser,
} from "@/lib/select-project-space";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";
import { isTopLogoOnboardingLocked } from "./onboarding-top-logo-lock";
import { useCompactSwitcherContextMenuRenderers } from "./compact-switcher-context-menus";
import { useDesktopMode } from "./use-desktop-mode";
import { restoreAndFocusCurrentWindow } from "./use-global-hotkey";
import { useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

interface TooltipState {
  label: string;
  left: number;
  top: number;
}

const TOOLTIP_ID = "desktop-compact-switcher-tooltip";

export function CompactProjectSpaceRow() {
  const t = useT(compactMessages);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [projectCreationPending, setProjectCreationPending] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const projects = useProjectStore((s) => s.projects);
  const currentProject = useProjectStore((s) => s.currentProject);
  const fetchAllProjects = useProjectStore((s) => s.fetchAllProjects);
  const onboardingStep = useOnboardingStore((s) => s.step);
  const onboardingStepHydrated = useOnboardingStore((s) => s.stepHydrated);
  const openWorkspace = useDesktopMode((s) => s.openWorkspace);
  const requestSettings = useDesktopMode((s) => s.requestSettings);
  const topLogoOnboardingLocked = isTopLogoOnboardingLocked({
    step: onboardingStep,
    stepHydrated: onboardingStepHydrated,
  });
  const activeProject =
    currentProject ??
    projects.find((project) => project.is_home) ??
    projects[0] ??
    null;
  const switcherProjects = useMemo<SwitcherProject[]>(
    () =>
      projects.map((project) => ({
        id: project.id,
        name: project.name,
        color: project.color || "#30D158",
        active: project.id === activeProject?.id,
        isHome: Boolean(project.is_home),
        isPinned: project.is_pinned,
        spaces: [],
      })),
    [activeProject?.id, projects],
  );
  const interactionDisabled = topLogoOnboardingLocked || projectCreationPending;

  useLayoutEffect(() => {
    const element = tooltipRef.current;
    if (!element || !tooltip) return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0) return;
    const edgePadding = 8;
    const correction =
      rect.left < edgePadding
        ? edgePadding - rect.left
        : rect.right > window.innerWidth - edgePadding
          ? window.innerWidth - edgePadding - rect.right
          : 0;
    if (correction === 0) return;
    setTooltip((current) =>
      current ? { ...current, left: current.left + correction } : current,
    );
  }, [tooltip]);

  const showTooltip = (label: string, target: HTMLButtonElement) => {
    const rect = target.getBoundingClientRect();
    const viewportPadding = 64;
    setTooltip({
      label,
      left: Math.min(
        Math.max(rect.left + rect.width / 2, viewportPadding),
        window.innerWidth - viewportPadding,
      ),
      top: rect.bottom + 8,
    });
  };

  const tooltipHandlers = (label: string) => ({
    "aria-describedby": TOOLTIP_ID,
    onBlur: () => setTooltip(null),
    onFocus: (event: FocusEvent<HTMLButtonElement>) =>
      showTooltip(label, event.currentTarget),
    onPointerEnter: (event: PointerEvent<HTMLButtonElement>) =>
      showTooltip(label, event.currentTarget),
    onPointerLeave: () => setTooltip(null),
    onContextMenu: () => setTooltip(null),
  });

  useEffect(() => {
    if (projects.length === 0) void fetchAllProjects();
  }, [fetchAllProjects, projects.length]);

  const finishSelection = () => {
    openWorkspace();
    restoreAndFocusCurrentWindow();
  };

  const handleSelectProject = (projectId: string) => {
    if (topLogoOnboardingLocked) return;
    const project = projects.find((candidate) => candidate.id === projectId);
    if (!project) return;
    selectProjectFromUser(project);
    finishSelection();
  };

  const handleAddProject = async () => {
    if (interactionDisabled) return;
    setProjectCreationPending(true);
    try {
      const project = await createProjectFromUser("compact-project-create");
      if (project) finishSelection();
    } finally {
      setProjectCreationPending(false);
    }
  };

  const { renderProjectContextMenu } = useCompactSwitcherContextMenuRenderers({
    openSettings: (target) => {
      requestSettings(target);
      restoreAndFocusCurrentWindow();
    },
  });

  return (
    <div
      id="desktop-compact-project-space-row"
      className="scrollbar-none flex h-11 w-full items-center gap-1.5 overflow-x-auto px-2"
      data-testid="desktop-compact-project-space-row"
    >
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className="grid h-7 w-7 shrink-0 place-items-center text-muted transition-colors hover:text-primary focus-visible:ring-2 disabled:opacity-40"
        data-testid="desktop-compact-add-project"
        aria-label={t("addProject")}
        aria-busy={projectCreationPending}
        disabled={interactionDisabled}
        onClick={() => void handleAddProject()}
        {...tooltipHandlers(t("addProject"))}
      >
        {projectCreationPending ? (
          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Icons.Plus className="h-3.5 w-3.5" />
        )}
      </Button>

      {projects.map((project) => {
        const active = project.id === activeProject?.id;
        const tooltipLabel = project.is_home ? t("homeProject") : project.name;
        const switcherProject = switcherProjects.find(
          (candidate) => candidate.id === project.id,
        );
        if (!switcherProject) return null;
        return (
          <ContextMenu key={project.id}>
            <ContextMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                className={cn(
                  "grid h-7 w-7 shrink-0 place-items-center rounded-control transition-colors focus-visible:ring-2 disabled:opacity-40",
                  active && "bg-white/10 ring-1 ring-white/20",
                )}
                data-testid={`desktop-compact-project-${project.id}`}
                aria-label={tooltipLabel}
                aria-pressed={active}
                disabled={interactionDisabled}
                onClick={() => handleSelectProject(project.id)}
                {...tooltipHandlers(tooltipLabel)}
              >
                <ProjectAvatar
                  name={project.name}
                  color={project.color || "#30D158"}
                  isHome={Boolean(project.is_home)}
                  shape="circle"
                  className={cn(
                    "h-6 w-6",
                    project.is_home && "denker-compact-home-project-avatar",
                  )}
                />
              </Button>
            </ContextMenuTrigger>
            {renderProjectContextMenu(switcherProject)}
          </ContextMenu>
        );
      })}

      {tooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            id={TOOLTIP_ID}
            role="tooltip"
            className="denker-glass-tooltip pointer-events-none fixed z-[100] max-w-[calc(100vw-16px)] -translate-x-1/2 break-words rounded-control border px-2.5 py-1 text-center text-canvas-md font-medium text-primary"
            data-testid={TOOLTIP_ID}
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            {tooltip.label}
          </div>,
          document.body,
        )}
    </div>
  );
}
